import { MetaFunction } from '@remix-run/react';
import type { LoaderFunctionArgs } from '@remix-run/node';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonLink } from '~/components/common/buttons/ButtonLink';
import ClientLogos from '~/components/common/ClientLogos';
import TextButton from '~/components/common/buttons/TextButton';
import Badge, { Variant } from '~/components/common/badges/Badge';
import Footer from '~/components/layouts/footer/Footer';
import Navbar from '~/components/layouts/navigation/Navbar';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import { navbarNoSearch } from '~/components/utils/navbarConfigs';
import { DEFAULT_LANGUAGE, useLanguageParam } from '~/config/i18n';
import mixpanelTrack from '~/hooks/useMixPanel';
import i18next from '~/services/i18next.server';

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const t = await i18next.getFixedT(params.lang || DEFAULT_LANGUAGE);
  return {
    metaTags: {
      title: t('aboutUs.metaTitle'),
      description: t('aboutUs.metaDescription'),
    },
    baseUrl: import.meta.env.VITE_API_BASE_URL,
    locale: params.lang || DEFAULT_LANGUAGE,
  };
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data?.metaTags.title },
    { tagName: 'link', rel: 'canonical', href: data?.baseUrl + '/' + data?.locale + '/about-us' },
    { tagName: 'link', rel: 'alternate', hrefLang: 'en', href: data?.baseUrl + '/en' + '/about-us' },
    { tagName: 'link', rel: 'alternate', hrefLang: 'fr', href: data?.baseUrl + '/fr' + '/about-us' },
    { tagName: 'link', rel: 'alternate', hrefLang: 'x-default', href: data?.baseUrl + '/en' + '/about-us' },
    { tagName: 'link', rel: 'icon', sizes: '32x32', href: data?.baseUrl + '/favicon-32x32.png' },
    { tagName: 'link', rel: 'icon', sizes: '16x16', href: data?.baseUrl + '/favicon-16x16.png' },
    { tagName: 'link', rel: 'mask-icon', href: data?.baseUrl + '/safari-pinned-tab.svg', color: '#1c1a1f' },
    { tagName: 'link', rel: 'manifest', href: data?.baseUrl + '/site.webmanifest' },
    { name: 'google', content: 'notranslate' },
    { name: 'msapplication-TileColor', content: '#1c1a1f' },
    { name: 'og:site_name', content: 'Fiveoffices' },
    { name: 'og:type', content: 'website' },
    { name: 'og:title', content: data?.metaTags.title },
    { name: 'twitter:title', content: data?.metaTags.title },
    { name: 'description', content: data?.metaTags.description },
    { name: 'og:description', content: data?.metaTags.description },
    { name: 'twitter:description', content: data?.metaTags.description },
    { name: 'twitter:card', content: data?.metaTags.title },
    { name: 'locale', content: data?.locale },
    { name: 'twitter:image', content: data?.baseUrl + '/media/content-images/office_2.jpg' },
    { name: 'og:locale', content: data?.locale },
    { name: 'og:image', content: data?.baseUrl + '/media/content-images/office_2.jpg' },
    { name: 'og:image:url', content: data?.baseUrl + '/media/content-images/office_2.jpg' },
    { name: 'og:image:width', content: 1600 },
    { name: 'og:image:height', content: 1333 },
  ];
};

export default function AboutUs() {
  const [showMoreTeam, setShowMoreTeam] = useState(false);
  const lang = useLanguageParam();
  const { t } = useTranslation();

  useEffect(() => {
    mixpanelTrack('0.about_us_load');
  }, []);

  return (
    <>
      <div className="about-us-page">
        <Navbar configuration={navbarNoSearch} />
        <main className="pt-16 pt-md-30 pb-50">
          <div className="container hero-container mb-20 mb-md-50">
            <h1 className="about-us-title fo-open-sauce mb-6">
              <span className="fo-highlight-gradient -italic">{t('aboutUs.title.firstHighlight')}</span>
              <br className="d-md-none" />
              <span> {t('aboutUs.title.afterFirstHighlight')}</span>
              <br className="d-md-none" />
              <span className="fo-highlight-gradient -accent-line-highlight -italic">
                {' '}
                {t('aboutUs.title.secondHighlight')}{' '}
              </span>
              <br className="d-md-none" />
              <span>{t('aboutUs.title.afterSecondHighlight')}</span>
            </h1>
            <div className="fo-display-4 -regular text-center">{t('aboutUs.subtitle')}</div>
          </div>

          <section className="container fixed-container-md mb-14 mb-md-30">
            <div className="row g-8">
              <div className="col-12 col-md-5">
                <div className="d-flex fo-border-bottom-15 border-accent-500 mt-2">
                  <h2 className="fo-display-3 fo-highlight-gradient">{t('aboutUs.ourObservation.title')}</h2>
                </div>
              </div>
              <div className="col-12 col-md-7">
                <h3 className="fo-display-2 mb-4">{t('aboutUs.ourObservation.subtitle')}</h3>
                <p>{t('aboutUs.ourObservation.body')}</p>
              </div>
            </div>
          </section>

          <section className="container fixed-container-md mb-14 mb-md-30 px-md-34 py-14">
            <div
              className="fo-display-2 text-center -medium"
              dangerouslySetInnerHTML={{
                __html: t('aboutUs.highlightOne.body'),
              }}
            />
          </section>

          <section className="container fixed-container-md mb-14 mb-md-30">
            <div className="row g-8 flex-md-row-reverse">
              <div className="col-12 col-md-5">
                <div className="d-flex fo-border-bottom-15 border-accent-500 mt-2 justify-content-end">
                  <h2 className="fo-display-3 text-end fo-highlight-gradient">{t('aboutUs.ourSolution.title')}</h2>
                </div>
              </div>
              <div className="col-12 col-md-7">
                <h3 className="fo-display-2 mb-4">{t('aboutUs.ourSolution.subtitle')}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t('aboutUs.ourSolution.body'),
                  }}
                />
              </div>
            </div>
          </section>

          <section className="container fixed-container-md mb-14 mb-md-30 py-14">
            <div
              className="fo-display-2 text-center -medium"
              dangerouslySetInnerHTML={{
                __html: t('aboutUs.highlightTwo.body'),
              }}
            />
          </section>

          <section className="container fixed-container-md mb-14 mb-md-30">
            <div className="row g-8">
              <div className="col-12 col-md-5">
                <div className="d-flex fo-border-bottom-15 border-accent-500 mt-2">
                  <h2 className="fo-display-3 fo-highlight-gradient">{t('aboutUs.ourCommitment.title')}</h2>
                </div>
              </div>
              <div className="col-12 col-md-7">
                <h3 className="fo-display-2 mb-4">{t('aboutUs.ourCommitment.subtitle')}</h3>
                <p>{t('aboutUs.ourCommitment.body')}</p>
              </div>
            </div>
          </section>

          <section
            className="container mb-14 mb-md-30"
            style={{ maxWidth: 1008 }}
          >
            <div className="bg-accent-500 rounded-3 py-12 px-6 d-flex flex-column align-items-center">
              <div
                className="fo-display-2 text-white mb-6 text-center"
                dangerouslySetInnerHTML={{
                  __html: t('aboutUs.confused.title'),
                }}
              />

              <p
                className="text-white -regular text-center"
                style={{ maxWidth: 592 }}
              >
                {t('aboutUs.confused.body')}
              </p>
            </div>
          </section>

          <section className="bg-light-grey-500 py-20 mb-14 mb-md-30">
            <div className="container fixed-conteiner-md">
              <h2 className="fo-display-2 text-center mb-14">{t('aboutUs.whyUs.title')}</h2>
              <div className="row g-8">
                <div className="col-12 col-md-6">
                  <div className="comparison-card-fiveoffices bg-white fo-border-sm border-accent-500 rounded-4 p-8">
                    <div className="d-flex gap-3 flex-wrap justify-content-between mb-6">
                      <h3 className="fo-display-3">{t('aboutUs.whyUs.comparison.fiveoffices.title')}</h3>
                      <Badge
                        extraClasses="align-self-start"
                        variant={Variant.ACCENT}
                        text={t('aboutUs.whyUs.comparison.fiveoffices.badge')}
                      />
                    </div>

                    <ul>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <li
                          className={`d-flex align-items-center ${i + 1 == 5 ? '' : 'mb-3'}`}
                          key={i}
                        >
                          <div className="argument-badge -pro me-3">
                            <SVGIcon
                              iconType="check"
                              height="14"
                              width="10"
                              viewBoxWidth="14"
                              viewBoxHeight="14"
                            />
                          </div>
                          <div className="text-1">{t(`aboutUs.whyUs.comparison.fiveoffices.argument${i + 1}`)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="bg-white fo-border-sm border-grey-200 rounded-4 p-8">
                    <h3 className="fo-display-3 mb-6">{t('aboutUs.whyUs.comparison.ourCompetitors.title')}</h3>
                    <ul>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <li
                          className={`d-flex align-items-center ${i + 1 == 5 ? '' : 'mb-3'}`}
                          key={i}
                        >
                          <div className="argument-badge Engineer -contra me-3">
                            <SVGIcon
                              iconType="cross"
                              height="14"
                              width="14"
                            />
                          </div>
                          <div className="text-1">{t(`aboutUs.whyUs.comparison.ourCompetitors.argument${i + 1}`)}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="container fixed-container-md mb-14 mb-md-30">
            <div className="row g-8 flex-md-row-reverse">
              <div className="col-12 col-md-5">
                <div className="d-flex justify-content-end fo-border-bottom-15 border-accent-500 mt-2">
                  <h2 className="fo-display-3 fo-highlight-gradient">{t('aboutUs.concreteResults.title')}</h2>
                </div>
              </div>
              <div className="col-12 col-md-7">
                <h3 className="fo-display-2 mb-4">{t('aboutUs.concreteResults.subtitle')}</h3>
                <p>{t('aboutUs.concreteResults.body')}</p>
              </div>
            </div>
          </section>

          <section className="container mb-14 mb-md-30">
            <div className="who-we-are-banner fo-border-sm border-grey-200 rounded-4 p-6 d-flex flex-column flex-md-row gap-8 align-items-md-center">
              <img
                src="/media-remix/about-us/misc-icon-building.svg"
                alt="Office building illustation"
                className="align-self-start"
              />
              <p
                className="color-primary"
                dangerouslySetInnerHTML={{ __html: t('aboutUs.whoWeAre.body') }}
              />
            </div>
          </section>

          <section
            className="container mb-14 mb-md-30"
            style={{ maxWidth: 1008 }}
          >
            <h2 className="fo-display-2 text-center mb-4">{t('aboutUs.meetOurTeam.title')}</h2>
            <p className="text-center mb-14">{t('aboutUs.meetOurTeam.subtitle')}</p>
            <div className="team-picture-wrapper">
              <img
                src="/media-remix/about-us/fo-team-picture.png"
                alt="Fiveoffices team"
                className="team-picture"
              />
            </div>
          </section>

          <section className="container fixed-container-md mb-10">
            <div className="p-md-7">
              <div className="fo-display-5 -medium text-center mb-8">{t('aboutUs.teamCompanies.title')}</div>
              <div className="d-flex flex-wrap gap-10 column-gap-18 align-items-center justify-content-center">
                {teamCompanies.map((company, i) => (
                  <img
                    key={i}
                    src={`/media-remix/about-us/team-companies/${company.fileName}`}
                    alt={company.alt}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="container fixed-container-md mb-14 mb-md-30">
            <div className="row g-6">
              {teamMembers.map((member, i) => (
                <div
                  className={`col-12 col-md-6 col-lg-4 ${i >= 4 ? (showMoreTeam ? '' : 'd-none d-md-block') : ''}`}
                  key={i}
                >
                  <div className="team-member-card">
                    <div className="fo-display-5 -medium mb-2">{member.name}</div>
                    <div className="text-1 -regular color-grey-600">{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="d-flex d-md-none justify-content-center mt-8">
              <TextButton
                size="sm"
                text={showMoreTeam ? t('aboutUs.teamMembers.showLess') : t('aboutUs.teamMembers.showMore')}
                onClick={() => setShowMoreTeam(!showMoreTeam)}
              />
            </div>
          </section>

          <section className="mb-14 mb-md-30">
            <div className="key-figures pt-20 pb-30">
              <h2 className="fo-display-2 text-center mb-14">{t('aboutUs.keyFigures.title')}</h2>
              <div className="container fixed-container-md">
                <div className="key-figures-container">
                  <div className="key-figure-box -fixed">
                    <div className="fo-display-1 -bold mb-2">{t('aboutUs.keyFigures.figure1.number')}</div>
                    <div className="fo-display-3 -semibold color-grey-500">{t('aboutUs.keyFigures.figure1.text')}</div>
                  </div>
                  <div className="key-figure-box">
                    <span className="fo-display-1 -bold mb-2">{t('aboutUs.keyFigures.figure2.number')} </span>
                    <br className="d-sm-none" />
                    <span className="fo-display-3 -semibold color-grey-500">
                      {t('aboutUs.keyFigures.figure2.text')}
                    </span>
                  </div>
                  <div className="key-figure-box">
                    <span className="fo-display-1 -bold mb-2">{t('aboutUs.keyFigures.figure3.number')} </span>
                    <br className="d-sm-none" />
                    <span className="fo-display-3 -semibold color-grey-500">
                      {t('aboutUs.keyFigures.figure3.text')}
                    </span>
                  </div>

                  <div className="key-figure-box">
                    <span className="fo-display-1 -bold mb-2">{t('aboutUs.keyFigures.figure4.number')} </span>
                    <br className="d-sm-none" />
                    <span className="fo-display-3 -semibold color-grey-500">
                      {t('aboutUs.keyFigures.figure4.text')}
                    </span>
                  </div>
                  <div className="key-figure-box">
                    <span className="fo-display-1 -bold mb-2">{t('aboutUs.keyFigures.figure5.number')} </span>
                    <br className="d-sm-none" />
                    <span className="fo-display-3 -semibold color-grey-500">
                      {t('aboutUs.keyFigures.figure5.text')}
                    </span>
                  </div>
                  <div className="key-figure-box -fixed">
                    <div className="fo-display-1 -bold mb-2">{t('aboutUs.keyFigures.figure6.number')}</div>
                    <div className="fo-display-3 -semibold color-grey-500">{t('aboutUs.keyFigures.figure6.text')}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="container fixed-container-md mb-14 mb-md-30">
            <h2 className="fo-display-2 text-center mb-14">{t('aboutUs.theyTrustUs.title')}</h2>
            <ClientLogos />
          </section>

          <section className="mb-50">
            <div className="bg-primary pt-23 pt-md-32 pb-33 pb-md-42">
              <div className="container fixed-container-md">
                <h2 className="fo-display-2 text-white text-center mb-4">{t('aboutUs.bookAMeeting.title')}</h2>
                <p className="-regular text-white text-center mb-14">{t('aboutUs.bookAMeeting.body')}</p>
                <div className="d-flex flex-column flex-md-row justify-content-center">
                  <ButtonLink
                    variant="accent"
                    size="xl"
                    to={t('aboutUs.bookAMeeting.cta.url')}
                    target="_blank"
                    reloadDocument={true}
                    onClick={() => mixpanelTrack('0.au_book_call')}
                  >
                    <span className="text-1"> {t('aboutUs.bookAMeeting.cta.label')}</span>
                    <SVGIcon
                      className="ms-2"
                      iconType="long-arrow-up-right"
                    />
                  </ButtonLink>
                </div>
              </div>
            </div>
          </section>

          <section className="container fixed-container-md">
            <h2 className="fo-display-2 text-center mb-4">{t('aboutUs.tryOurPlatform.title')}</h2>
            <p className="-regular text-center mb-14">{t('aboutUs.tryOurPlatform.body')}</p>
            <div className="d-flex gap-8 flex-column flex-md-row justify-content-center">
              <ButtonLink
                variant="accent"
                size="xl"
                to={`/${lang}/signup/guest`}
                onClick={() => mixpanelTrack('0.au_find_an_office')}
              >
                <span className="text-1"> {t('aboutUs.tryOurPlatform.findAnOffice.label')}</span>
              </ButtonLink>
              <ButtonLink
                variant="accentOutline"
                size="xl"
                to={`/${lang}/signup/host`}
                onClick={() => mixpanelTrack('0.au_publish_an_office')}
              >
                <span className="text-1"> {t('aboutUs.tryOurPlatform.publishAnOffice.label')}</span>
              </ButtonLink>
            </div>
          </section>
        </main>
        <footer className="container-fluid p-0">
          <Footer />
        </footer>
      </div>
    </>
  );
}

const teamCompanies = [
  {
    alt: 'Dennemeyer',
    fileName: 'dennemeyer.svg',
  },
  {
    alt: 'Airbnb',
    fileName: 'airbnb.svg',
  },
  {
    alt: 'Spuerkeess',
    fileName: 'spuerkeess.svg',
  },
  {
    alt: 'Pledg',
    fileName: 'pledg.svg',
  },
  {
    alt: 'PWC',
    fileName: 'pwc.svg',
  },
  {
    alt: 'Barclays',
    fileName: 'barclays.svg',
  },
  {
    alt: 'Company 7',
    fileName: 'roland-berger.svg',
  },
  {
    alt: 'Pleo',
    fileName: 'pleo.svg',
  },
  {
    alt: 'Talkwalker',
    fileName: 'talkwalker.svg',
  },
  {
    alt: 'Geodis',
    fileName: 'geodis.svg',
  },
  {
    alt: 'Deutsche Bank',
    fileName: 'deutsche-bank.svg',
  },
];

const teamMembers = [
  { name: 'Bébéto Kponton', role: 'Account Executive' },
  { name: 'Tiffany Pichon', role: 'Host & Guest Success Manager' },
  { name: 'Andrea Ragghianti', role: 'UI/UX Designer' },
  { name: 'Niko Immonen', role: 'Account Executive' },
  { name: 'Ilan Freising', role: 'Head of Sales' },
  { name: 'Dmitri Barabanov', role: 'Senior Software Engineer' },
  { name: 'Max Sevenig', role: 'Software Engineer' },
  { name: 'Maxime Jacques', role: 'Head of Product' },
  { name: 'Camille Segard', role: 'Host Success Representative' },
  { name: 'Kim Laplume', role: 'Tech Lead' },
  { name: 'Valentin Hernoux', role: 'SEA Manager' },
  { name: 'Quentin Branger', role: 'Account Executive' },
  { name: 'Philippe Coulon', role: 'CEO' },
  { name: 'Alejandro Vilgrain', role: 'Host & Supply Manager' },
  { name: 'Pierre Harington', role: 'Growth Hacking Manager' },
  { name: 'Aisling McCaffrey', role: 'Marketing Technology' },
  { name: 'Jeni Percic', role: 'Senior Software Engineer' },
  { name: 'Valentine Chedru', role: 'Head of Marketing' },
  { name: 'Marion Sebaux', role: 'Host & Guest Success Manager' },
  { name: 'Aline Boullot', role: 'Sales Development Representative' },
  { name: 'Osman Koné', role: 'Account Executive' },
];
